import React, { useEffect, useState } from 'react';
import { Avatar } from '../../../../components/ui';
import { getThemeColors } from '../../../../lib/theme';
import { navigateGlobal, buildPath } from '../../../../lib/route';
import { rowLinkProps } from '../../../../lib/openLink';
import { describeEntry, listPbTicker, type ActivityProtocolEntry } from '../../../../lib/activityProtocol';

interface PbTickerWidgetProps {
  mode: 'full' | 'summary';
  dark: boolean;
  tr: (k: string, opts?: Record<string, unknown>) => string;
  onTotal?: (total: number | null) => void;
}

const REFRESH_MS = 60_000;

/**
 * Feedback 11.10.2026 — „PB-Ticker" ersetzt die Kachel „Pflegebedürftige".
 *
 * Je Pflegebedürftigem die jüngste fachliche Änderung aus dem Aktionsprotokoll
 * (wer hat wann was gemacht), sortiert nach der neuesten Änderung absteigend.
 * Klick öffnet das PB-Profil. Aktualisiert sich jede Minute.
 */
export function PbTickerWidget({ mode, dark, tr, onTotal }: PbTickerWidgetProps) {
  const t = getThemeColors(dark);
  const [rows, setRows] = useState<ActivityProtocolEntry[] | null>(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    let alive = true;
    const load = () => {
      listPbTicker(mode === 'summary' ? 5 : 20)
        .then(data => { if (alive) { setRows(data); setError(false); onTotal?.(data.length); } })
        .catch(() => { if (alive) { setError(true); onTotal?.(null); } });
    };
    load();
    const timer = setInterval(load, REFRESH_MS);
    return () => { alive = false; clearInterval(timer); };
    // onTotal ist ein stabiler Setter aus WidgetCard.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode]);

  if (error) {
    return <div style={{ fontSize: 12, color: t.muted }}>{tr('dashboard.widget.pb_ticker.error')}</div>;
  }
  if (rows === null) {
    return <div style={{ fontSize: 12, color: t.muted }}>{tr('dashboard.widget.loading')}</div>;
  }
  if (rows.length === 0) {
    return <div style={{ fontSize: 12, color: t.muted }}>{tr('dashboard.widget.pb_ticker.empty')}</div>;
  }

  const shown = mode === 'summary' ? rows.slice(0, 5) : rows;

  return (
    <div data-testid="pb-ticker" style={{ display: 'flex', flexDirection: 'column' }}>
      {shown.map((e, i) => {
        const name = e.entity_label ?? (e.entity_user_id != null ? `#${e.entity_user_id}` : '—');
        const open = () => { if (e.entity_user_id != null) navigateGlobal('pflege', String(e.entity_user_id)); };
        return (
          <div
            key={e.id}
            role="button"
            tabIndex={0}
            {...rowLinkProps(e.entity_user_id != null ? buildPath('pflege', String(e.entity_user_id)) : null, open)}
            onKeyDown={ev => { if (ev.key === 'Enter' || ev.key === ' ') { ev.preventDefault(); open(); } }}
            style={{
              display: 'flex', alignItems: 'flex-start', gap: 10, padding: '9px 0', cursor: 'pointer',
              borderBottom: i < shown.length - 1 ? `1px solid ${t.borderSoft}` : 'none',
            }}
          >
            <Avatar name={name} userId={e.entity_user_id ?? null} size={30} dark={dark} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: t.heading, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {name}
              </div>
              <div style={{ fontSize: 11.5, color: t.muted, lineHeight: 1.35, marginTop: 1, wordBreak: 'break-word' }}>
                {describeEntry(e, tr, false, true)}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
